import type { SeasonStats } from './sleeperApi';
import { fetchSeasonStats, fetchPlayerProjections, seasonFantasyPoints } from './sleeperApi';
import type { TeamOffensiveStats, LeagueAverages, PlayerStats } from './playerValue';

/**
 * Process raw Sleeper API data into mapped stats and analytics.
 */
export async function getProcessedSleeperData(
  season: number,
  seasonType: 'regular' | 'post' | 'pre' | 'off' = 'regular'
): Promise<{
  statsMap: Map<string, SeasonStats>;
  projections: Record<string, number>;
  playerStatsMap: Map<string, PlayerStats>;
  teamUsageMap: Map<string, PlayerStats>;
  teamStatsMap: Map<string, TeamOffensiveStats>;
  leagueAverages: LeagueAverages;
}> {
  let stats: SeasonStats[] = [];
  try {
    stats = await fetchSeasonStats(season, seasonType);
    if (stats.length === 0 && season > 2020) {
      stats = await fetchSeasonStats(season - 1, seasonType);
    }
  } catch (e) {
    console.error('fetchSeasonStats error:', e);
  }
  // Fetch projections with fallback to empty object
  let projections: Record<string, number> = {};
  try {
    projections = await fetchPlayerProjections(season, seasonType);
  } catch (e) {
    console.error('fetchPlayerProjections error:', e);
  }

  const statsMap = new Map<string, SeasonStats>();
  stats.forEach((s) => {
    if (!s.player_id) return;
    statsMap.set(String(s.player_id), {
      ...s,
      fpts_ppr: seasonFantasyPoints(s),
    });
  });

  const playerStatsMap = new Map<string, PlayerStats>();
  stats.forEach((s) => {
    if (!s.player_id) return;
    const targets = s.rec_tgt || s.rec_tar || s.rec || 0;
    playerStatsMap.set(String(s.player_id), {
      targets,
      carries: s.rush_att || 0,
      redZoneOpportunities: s.rec_rz_tgt ?? s.redzone_targets ?? targets,
      airYards: s.rec_air_yd || s.air_yards || 0,
      schedule: []
    });
  });

  // Aggregate by team
  const usage: Record<string, { targets: number; carries: number; redZone: number; airYards: number }> = {};
  stats.forEach((s) => {
    const team = s.team || 'FA';
    if (!usage[team]) usage[team] = { targets: 0, carries: 0, redZone: 0, airYards: 0 };
    usage[team].targets += s.rec_tar || 0;
    usage[team].carries += s.rush_att || 0;
    usage[team].redZone += s.redzone_targets ?? (s.rec_tar || 0) + (s.rush_att || 0);
    usage[team].airYards += s.air_yards || 0;
  });

  // Map per-team player stats share
  const teamUsageMap = new Map<string, PlayerStats>();
  Object.entries(usage).forEach(([team, u]) => {
    teamUsageMap.set(team, {
      targets: u.targets,
      carries: u.carries,
      redZoneOpportunities: u.redZone,
      airYards: u.airYards,
      schedule: []
    });
  });

  // Build TeamOffensiveStats and league averages
  const teamStatsMap = new Map<string, TeamOffensiveStats>();
  const statsList: TeamOffensiveStats[] = [];
  usage && Object.entries(usage).forEach(([team, u]) => {
    const plays = u.targets + u.carries;
    // rough placeholder for points per game
    const points = plays * 0.5;
    const rzRate = plays > 0 ? u.redZone / plays : 0;
    const tos: TeamOffensiveStats = {
      playsPerGame: plays,
      pointsPerGame: points,
      redZoneScoreRate: rzRate,
      offensiveLineRank: 16
    };
    teamStatsMap.set(team, tos);
    statsList.push(tos);
  });

  const len = statsList.length || 1;
  const leagueAverages: LeagueAverages = {
    playsPerGame: statsList.reduce((s, t) => s + t.playsPerGame, 0) / len,
    pointsPerGame: statsList.reduce((s, t) => s + t.pointsPerGame, 0) / len,
    redZoneScoreRate: statsList.reduce((s, t) => s + t.redZoneScoreRate, 0) / len
  };

  return { statsMap, projections, playerStatsMap, teamUsageMap, teamStatsMap, leagueAverages };
}
